/**
 * Centralized icon mappings using Lucide React.
 *
 * All icons used across the app are exported from here.
 * Import individual icons or use the category/action maps.
 */

import {
  MessageCircle,
  BookOpen,
  Library,
  FlaskConical,
  Mail,
  Bot,
  BarChart3,
  RefreshCw,
  User,
  Users,
  Pencil,
  Key,
  Ban,
  Check,
  CheckCircle,
  X,
  XCircle,
  Trash2,
  Package,
  Sprout,
  HelpCircle,
  FileEdit,
  FileText,
  Search,
  MessageSquare,
  ClipboardList,
  Globe,
  Paperclip,
  Image,
  DollarSign,
  Settings,
  Star,
  ThumbsUp,
  ThumbsDown,
  TrendingUp,
  Lightbulb,
  Link,
  Smartphone,
  Save,
  Brain,
  AlertTriangle,
  Files,
  FileSpreadsheet,
  GitBranch,
  LogOut,
  Square,
  Plus,
  Clock,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  CornerDownRight,
  Menu,
  Maximize2,
  Copy,
  Send,
  CircleStop,
  Eye,
  EyeOff,
  History,
  Archive,
  RotateCcw,
  Bookmark,
  Play,
  Pause,
  Download,
  Upload,
  Filter,
  MoreHorizontal,
  Info,
  Wrench,
  SlidersHorizontal,
  CircleDot,
  Minus,
  Inbox,
  Sparkles,
  Timer,
  ArrowDownToLine,
  ArrowUpToLine,
  Coins,
  BookOpenCheck,
  ShieldAlert,
  CircleAlert,
  Gauge,
  SquareX,
  ExternalLink,
  Share2,
  Zap,
  FolderKanban,
  Bold,
  Italic,
  List,
  ListOrdered,
  Undo,
  Redo,
  LayoutGrid,
  LayoutDashboard,
  GanttChart,
  Tag,
  ArrowLeft,
  CheckCircle2,
  Loader,
  Circle,
  Edit3,
  Calendar,
  GripVertical,
  Map,
  ListChecks,
  ArrowRight,
  Wifi,
  Shield,
  ArrowUp,
  Palette,
  AtSign,
  MessageSquareQuote,
  ArrowUpDown,
  Code,
  Braces,
  FileDown,
  FileUp,
  CheckSquare,
  Bug,
  Lock,
  Monitor,
  Sun,
  Moon,
  Terminal,
  FolderOpen,
  Folder,
  FolderPlus,
  ClipboardCopy,
  MonitorSmartphone,
  ScrollText,
  ShoppingBag,
  Cpu,
  Heart,
  Layers,
  Server,
  WifiOff,
  Loader2,
  ArrowDown,
  FileSearch,
  Cloud,
  LogIn,
  Calculator,
  Pin,
  PinOff,
  Building2,
  Megaphone,
  ListTodo,
  UserPlus,
  GitFork,
  Bluetooth,
  Plug,
  Unplug,
  Signal,
  Radio,
  Video,
  Crown,
  CheckCheck,
  type LucideIcon,
} from 'lucide-react';

// Re-export for direct usage
export {
  MessageCircle,
  BookOpen,
  Library,
  FlaskConical,
  Mail,
  Bot,
  BarChart3,
  RefreshCw,
  User,
  Users,
  Pencil,
  Key,
  Ban,
  Check,
  CheckCircle,
  X,
  XCircle,
  Trash2,
  Package,
  Sprout,
  HelpCircle,
  FileEdit,
  FileText,
  Search,
  MessageSquare,
  ClipboardList,
  Globe,
  Paperclip,
  Image,
  DollarSign,
  Settings,
  Star,
  ThumbsUp,
  ThumbsDown,
  TrendingUp,
  Lightbulb,
  Link,
  Smartphone,
  Save,
  Brain,
  AlertTriangle,
  Files,
  FileSpreadsheet,
  GitBranch,
  LogOut,
  Square,
  Plus,
  Clock,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  CornerDownRight,
  Menu,
  Maximize2,
  Copy,
  Send,
  CircleStop,
  Eye,
  EyeOff,
  History,
  Archive,
  RotateCcw,
  Bookmark,
  Play,
  Pause,
  Download,
  Upload,
  Filter,
  MoreHorizontal,
  Info,
  Wrench,
  SlidersHorizontal,
  CircleDot,
  Minus,
  Inbox,
  Sparkles,
  Timer,
  ArrowDownToLine,
  ArrowUpToLine,
  Coins,
  BookOpenCheck,
  ShieldAlert,
  CircleAlert,
  Gauge,
  SquareX,
  ExternalLink,
  Share2,
  Zap,
  FolderKanban,
  Bold,
  Italic,
  List,
  ListOrdered,
  Undo,
  Redo,
  LayoutGrid,
  LayoutDashboard,
  GanttChart,
  Tag,
  ArrowLeft,
  CheckCircle2,
  Loader,
  Circle,
  Edit3,
  Calendar,
  GripVertical,
  Map,
  ListChecks,
  ArrowRight,
  Wifi,
  Shield,
  Crown,
  ArrowUp,
  Palette,
  AtSign,
  MessageSquareQuote,
  ArrowUpDown,
  Code,
  Braces,
  FileDown,
  FileUp,
  CheckSquare,
  Bug,
  Lock,
  Monitor,
  Sun,
  Moon,
  Terminal,
  FolderOpen,
  Folder,
  FolderPlus,
  ClipboardCopy,
  MonitorSmartphone,
  ScrollText,
  ShoppingBag,
  Cpu,
  Heart,
  Layers,
  Server,
  WifiOff,
  Loader2,
  ArrowDown,
  FileSearch,
  Cloud,
  LogIn,
  Calculator,
  Pin,
  PinOff,
  Building2,
  Megaphone,
  ListTodo,
  UserPlus,
  GitFork,
  Bluetooth,
  Plug,
  Unplug,
  Signal,
  Radio,
  Video,
  CheckCheck,
};
export type { LucideIcon };

// ─── Navigation Icons ────────────────────────────────────

export const NAV_ICONS: Record<string, LucideIcon> = {
  chat: MessageCircle,
  wiki: BookOpen,
  sync: RefreshCw,
};

// ─── Content Category Icons ──────────────────────────────

export const CATEGORY_ICONS: Record<string, LucideIcon> = {
  product: Package,
  plant: Sprout,
  faq: HelpCircle,
  blog: FileEdit,
  topic: Files,
};

/** Get the Lucide icon for a content category. */
export function getCategoryIcon(category: string): LucideIcon {
  return CATEGORY_ICONS[category] || FileText;
}

// ─── Context Type Icons ─────────────────────────────────

export const CONTEXT_ICONS: Record<string, LucideIcon> = {
  'wiki-detail': FileText,
  'wiki-list': BookOpen,
  chat: MessageCircle,
  history: ClipboardList,
  dashboard: LayoutDashboard,
};

/** Get the Lucide icon for a page context type. */
export function getContextIcon(type: string): LucideIcon {
  return CONTEXT_ICONS[type] || FileText;
}

// ─── Tool Icons ─────────────────────────────────────────

export const TOOL_ICONS: Record<string, LucideIcon> = {
  search: Search,
  get_page: FileText,
  update_page: Pencil,
  knowledge_query: Library,
  knowledge_mutation: Pencil,
  analyze_image: Image,
  external_search: Globe,
  ask_user: ClipboardList,
  ecommerce: ShoppingBag,
  feature_request: Lightbulb,
  generate_image: Palette,
  project_manager: FolderKanban,
  email_manager: Mail,
  compute: Calculator,
};

/** Get the Lucide icon for a tool name. */
export function getToolIcon(toolName: string): LucideIcon {
  return TOOL_ICONS[toolName] || Wrench;
}

// ─── Tool Brief Descriptions ─────────────────────────────

export const TOOL_BRIEFS: Record<string, string> = {
  search: 'Search knowledge base for products, guides & troubleshooting',
  knowledge_query: 'Search and read team, personal, and shared knowledge documents',
  knowledge_mutation: 'Create or update knowledge base documents',
  get_page: 'Read a specific wiki page in detail',
  analyze_image: 'Analyze plant/product photos for diagnosis',
  external_search: 'Search the internet for latest information',
  update_page: 'Create or update wiki pages',
  ask_user: 'Ask the user clarifying questions',
  ecommerce: 'Query ecommerce store data (Shopify/Amazon)',
  feature_request: 'Submit and manage feature requests',
  generate_image: 'Generate images from text descriptions',
  project_manager: 'Create and manage projects & tasks',
  email_manager: 'Read, search and send emails from bound accounts',
  compute: 'Execute code for precise data analysis & calculations',
};

/** Get brief description for a tool. */
export function getToolBrief(toolName: string): string {
  return TOOL_BRIEFS[toolName] || toolName;
}

// ─── Action Change Type Icons ────────────────────────────

export const CHANGE_TYPE_ICONS: Record<string, LucideIcon> = {
  new: Plus,
  added: Plus,
  updated: Pencil,
  deleted: Trash2,
};

// ─── Status Icons ────────────────────────────────────────

export const STATUS_ICONS: Record<string, LucideIcon> = {
  running: RefreshCw,
  done: CheckCircle,
  error: XCircle,
  completed: CheckCircle,
  cancelled: CircleStop,
  pending: Clock,
};

// ─── Capability Icons (Profile quick-action buttons) ─────

/** Icon name → Lucide component lookup for profile capabilities. */
export const CAPABILITY_ICON_MAP: Record<string, LucideIcon> = {
  Search,
  Sprout,
  Image,
  Bot,
  Globe,
  FileEdit,
  BarChart3,
  HelpCircle,
  Package,
  BookOpen,
  FlaskConical,
  ClipboardList,
  Mail,
  Lightbulb,
  Pencil,
  AlertTriangle,
  Plus,
  TrendingUp,
  Building2,
  Megaphone,
  ListTodo,
  UserPlus,
  FolderKanban,
  LayoutDashboard,
  ShoppingBag,
  FileText,
  Palette,
};

/** Ordered list of capability icons for picker UI. */
export const CAPABILITY_ICON_LIST: { name: string; Icon: LucideIcon }[] = Object.entries(CAPABILITY_ICON_MAP).map(
  ([name, Icon]) => ({ name, Icon }),
);

/** Resolve a capability icon name to a Lucide component. Falls back to Sprout. */
export function resolveCapabilityIcon(name: string): LucideIcon {
  return CAPABILITY_ICON_MAP[name] || Sprout;
}
